import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, Typography, useTheme } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import ApprovalSideBar from "./ApprovalSideBar";
import MasterSidebar from "./MasterSidebar";
// import BankReconciliationSidebar from "./BankRecoSidebar";

// ------------- Mui Icons --------------------
import HomeIcon from "@mui/icons-material/Home";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MenuOpen from "@mui/icons-material/MenuOpen";
import AddHomeWorkRoundedIcon from "@mui/icons-material/AddHomeWorkRounded";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const Item = ({ title, to, icon, selected, setSelected, style }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.primary[100],
        ...style,
        backgroundColor:
          selected === title ? colors.blueAccent[900] : "transparent",
        borderRadius: "10px",
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography
        style={{
          fontWeight: "bold",
          color: style?.color || colors.primary[100],
          fontSize: "16px",
        }}
      >
        {title}
      </Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  
  // Initialize selected from localStorage or default to "Dashboard"
  const [selected, setSelected] = useState(() => {
    const saved = localStorage.getItem("sidebarSelectedItem");
    return saved || "Dashboard";
  });
  
  const token = localStorage.getItem("token");
  const [showAllUsers, setshowAllUsers] = useState(false);
  const [userData, setUserData] = useState([]);
  const role = localStorage.getItem("role");

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Save selected item to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarSelectedItem", selected);
  }, [selected]);

  // Sync selected item with current route on component mount and route changes
  useEffect(() => {
    const pathToTitleMap = {
      "/Infinite/Dashboard": "Dashboard",
      "/Infinite/TallyCompanies": "Tally Companies",
      "/Infinite/Users": "Users",
      "/Infinite/Ledger": "Ledger",
      "/Infinite/LedGroup": "Groups",
      "/Infinite/VoucherType": "Voucher Type",
      "/Infinite/PrimaryApprov": "Primary Voucher",
      "/Infinite/SecondaryApprov": "Secondary Voucher",
      "/Infinite/FinalApprov": "Final Voucher",
      "/Infinite/LedgerPrimaryApp": "Primary Ledger",
      "/Infinite/LedgerFinalApp": "Final Ledger",
      "/Infinite/StkItemFinalApp": "Final Stock Items",
      "/Infinite/AdminList": "Approval Report",
      "/Infinite/ApprovalModuleUsers": "Users Config",
      "/Member/ShraddhaEvents": "Events",
      "/Member/Support": "Support"
    };

    const currentTitle = pathToTitleMap[location.pathname];
    if (currentTitle && currentTitle !== selected) {
      setSelected(currentTitle);
    }
  }, [location.pathname, selected]);

  useEffect(() => {
    fetchUserData();
  }, [token]);

  const fetchUserData = async () => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/profile/info",
        { role },
        {
          headers: { Authorization: token },
        }
      );
      setUserData(response.data.user[0]);
      localStorage.setItem("userRole", response.data.user[0].role);
    } catch (err) {
      alert("Error fetching user data");
    }
  };

  useEffect(() => {
    if (!userData?.role) return;

    if (userData.role === "admin") {
      setshowAllUsers(true);
    }
  }, [userData]);

  const handleEvent = () => {
    setSelected("Events");
    navigate("/Member/ShraddhaEvents");
  };

  const handlSupport = () => {
    setSelected("Support");
    navigate("/Member/Support");
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          "& .pro-sidebar-inner": {
            background: `${colors.primary[400]} !important`,
            boxShadow: "2px 0 2px -2px rgba(0, 0, 0, 0.5)",
            marginTop: {
              xs: "4px",
              sm: "4px",
              md: "4px",
              lg: "24px",
              xl: "4px",
            },
            position: "fixed",
            height: isCollapsed ? "78vh" : "80vh",
            overflowY: "auto",
            top: isCollapsed ? "40px" : "40px",
            left: 0,
            width: {
              xs: isCollapsed ? "70px" : "220px",
              sm: isCollapsed ? "70px" : "230px",
              md: isCollapsed ? "70px" : "240px",
              lg: isCollapsed ? "70px" : "240px",
            },
            zIndex: 999,
            transition: "all 0.3s ease",
            flexDirection: "column",
            // Custom scrollbar styles
            "&::-webkit-scrollbar": {
              width: "1px",
            },
            "&::-webkit-scrollbar-track": {
              // background: colors.primary[400],
              borderRadius: "1px",
            },
            "&::-webkit-scrollbar-thumb": {
              // background: colors.blueAccent[700],
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              // background: colors.blueAccent[500],
            },
            // Firefox scrollbar
            scrollbarWidth: "thin",
            // scrollbarColor: `${colors.blueAccent[700]} ${colors.primary[400]}`,
          },
          "& .pro-icon-wrapper": {
            backgroundColor: "transparent !important",
          },
          "& .pro-inner-item": {
            color: `${colors.grey[400]}!important`,
          },
          "& .pro-inner-item:hover": {
            color: "#CBAD5B !important",
          },
          "& .pro-menu-item": {
            color: "#6870fa !important",
            borderRadius: "10px",
            fontSize: "7px",
          },
          "& .pro-menu-item.active": {
            color: "#6870fa !important",
            backgroundColor: `${colors.blueAccent[900]}!important`,
            borderRadius: "10px",
          },
        }}
      >
        <ProSidebar collapsed={isCollapsed}>
          <Menu iconShape="square">
            {/* Sticky Toggle Menu Item */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1000,
                backgroundColor: colors.primary[400],
                padding: "0px",
                margin: 0,
              }}
            >
              <MenuItem
                onClick={() => setIsCollapsed(!isCollapsed)}
                icon={isCollapsed ? <MenuOutlinedIcon /> : <MenuOpen />}
                style={{
                  backgroundColor: 'transparent',
                  color: colors.grey[100],
                  padding: 0,
                  margin: 0,
                  minHeight: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                }}
              ></MenuItem>
            </Box>

            {/* Menu Content */}
            <Box sx={{ paddingTop: "0px" }}>
              <Item
                title="Dashboard"
                to="/Infinite/Dashboard"
                icon={<HomeIcon fontSize="small" />}
                selected={selected}
                setSelected={setSelected}
              />
              <Item
                title="Tally Companies"
                to="/Infinite/TallyCompanies"
                icon={<AddHomeWorkRoundedIcon fontSize="small" />}
                selected={selected}
                setSelected={setSelected}
              />
              {showAllUsers && (
                <Item
                  title="Users"
                  to="/Infinite/Users"
                  icon={<PeopleOutlinedIcon fontSize="small" />}
                  selected={selected}
                  setSelected={setSelected}
                />
              )}

              <MasterSidebar
                isCollapsed={isCollapsed}
                selected={selected}
                setSelected={setSelected}
              />
              <ApprovalSideBar
                isCollapsed={isCollapsed}
                selected={selected}
                setSelected={setSelected}
              />
              {/* <BankReconciliationSidebar
                isCollapsed={isCollapsed}
                selected={selected}
                setSelected={setSelected}
              /> */}
            </Box>
          </Menu>
        </ProSidebar>
      </Box>
      <Box
        sx={{
          background: `${colors.primary[400]} !important`,
          boxShadow: "2px 0 2px -2px rgba(0, 0, 0, 0.5)",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: {
            xs: isCollapsed ? "70px" : "220px",
            sm: isCollapsed ? "70px" : "230px",
            md: isCollapsed ? "70px" : "240px",
            lg: isCollapsed ? "70px" : "240px",
          },
          display: isCollapsed ? "" : "flex",
          flexDirection: isCollapsed ? "column" : "row",
          alignItems: "center",
          padding: "10px",
          zIndex: 1000,
          height: "13.5vh",
          // Custom scrollbar for bottom section if needed
          "&::-webkit-scrollbar": {
            width: "1px",
          },
          "&::-webkit-scrollbar-track": {
            background: colors.primary[400],
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.blueAccent[700],
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: colors.blueAccent[500],
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${colors.blueAccent[700]} ${colors.primary[400]}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginRight: "10px",
            marginLeft: "10px",
            cursor: "pointer",
          }}
          onClick={handleEvent}
        >
          <EventNoteIcon style={{ color: "#B09246" }} />
          {!isCollapsed && (
            <Typography sx={{ marginLeft: "8px", color: "#B09246" }}>
              Events
            </Typography>
          )}
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={handlSupport}
        >
          <SupportAgentIcon
            style={{ color: "#B09246", marginLeft: isCollapsed ? "10px" : "0" }}
          />
          {!isCollapsed && (
            <Typography sx={{ marginLeft: "8px", color: "#B09246" }}>
              Support
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;